import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { filenameToFontAwesomeIcon } from '../../../../../../utils/io/file';
import { AssetDownloadManager } from '../../../../../../viewer/elements/asset-download-manager';
import { Dxf } from '../../../../../../viewer/elements/dxf';
import { Glb } from '../../../../../../viewer/elements/glb';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useSharedView } from '../../../../../hooks/use-shared-view';
import { Icon } from '../../../../icon/icon';
import { MoveIcon } from '../../../../skyview/right-menu/components/move-icon';
import { useSubscribeShowDeleteAssetDialog } from '../../../hooks/subscribe-show-delete-asset-dialog';
import { AssetHeader } from '../../components/asset-blocks/asset-header';
import { DeleteAssetDialog } from '../../dialogs/delete-asset-dialog';
import { DisplaySettingsDialog, DisplaySettingsIcon } from '../../dialogs/display-settings-dialog';
import {
  hasLayers,
  LayerVisibilityDialog,
  LayerVisibilityIcon,
  useOpenLayerVisibilityOnSelection,
} from '../../dialogs/layer-visibility-dialog';
import { DeleteIcon } from '../../icons/delete-icon';
import { DownloadIcon } from '../../icons/download-icon';

type Props = {
  asset: Dxf | Glb;
};

const CadHeader = (props: Props) => {
  const { t } = useTranslation();
  const displaySettingsDialog = useDialog();
  const layerVisibilityDialog = useDialog();
  const glb = props.asset instanceof Glb ? props.asset : undefined;
  const [isDownloading, setIsDownloading] = React.useState(false);
  const { sharedViewId, isSharedView } = useSharedView();
  const deleteDialog = useDialog();
  useSubscribeShowDeleteAssetDialog(props.asset, deleteDialog, isSharedView);
  useOpenLayerVisibilityOnSelection(glb, layerVisibilityDialog);

  const match = /(.+?) - version #(\d+)$/i;
  const name = props.asset.name.match(match)![1];
  const version = parseInt(props.asset.name.match(match)![2]);
  const assetLoadedAndVisible =
    props.asset.getStateVisibility() && props.asset.loadingStatus === 'loaded';

  const deleteIcon = (
    <DeleteIcon
      key="deleteIcon"
      onClick={(e) => deleteDialog.show({ x: e.clientX, y: e.clientY })}
    />
  );

  // The DXF of a DWG or DXF file, also for the glb asset of one: the backend converts drawings to both.
  const canDownloadDxf =
    props.asset instanceof Dxf ||
    (props.asset instanceof Glb && props.asset.getFileType() === 'Dwg');

  const downloadIcon = canDownloadDxf ? (
    <DownloadIcon
      key="downloadIcon"
      loading={isDownloading}
      title={
        isDownloading
          ? t('oneMoment', { ns: 'common' })
          : t('cad.icons.download', { ns: 'skyviewAssetMenu' })
      }
      onClick={async () => {
        // Error handling is included in downloadDxfAsset.
        try {
          setIsDownloading(true);
          await new AssetDownloadManager().downloadDxfAsset(
            props.asset,
            undefined,
            undefined,
            sharedViewId,
          );
        } finally {
          setIsDownloading(false);
        }
      }}
    />
  ) : (
    <></>
  );

  // IFC models from ConvertIfcToGlb are shown as they are, which is known once they have loaded.
  const ifcWarningIcon =
    props.asset instanceof Glb && props.asset.isFromLegacyIfcConverter() ? (
      <Icon
        disabled={true}
        icon="exclamation-triangle"
        title={t('cad.icons.ifcWarning', { ns: 'skyviewAssetMenu' })}
      />
    ) : (
      <></>
    );

  const moveIcon =
    props.asset instanceof Glb && props.asset.canBeTransformed() ? (
      <MoveIcon asset={props.asset} disabled={!assetLoadedAndVisible} key="moveIcon" />
    ) : (
      <></>
    );

  const displaySettingsIcon =
    props.asset instanceof Glb ? (
      <DisplaySettingsIcon
        disabled={!assetLoadedAndVisible}
        key="displaySettings"
        onClick={(e) => displaySettingsDialog.show()}
      />
    ) : (
      <></>
    );

  const showLayers = glb && hasLayers(glb);
  const layerVisibilityIcon = showLayers ? (
    <LayerVisibilityIcon
      key="layerVisibility"
      onClick={(e) => layerVisibilityDialog.show({ x: e.clientX, y: e.clientY })}
    />
  ) : (
    <></>
  );

  const { icon, color } = filenameToFontAwesomeIcon(name);

  return (
    <>
      <AssetHeader
        additionalIcons={
          isSharedView
            ? showLayers
              ? [layerVisibilityIcon]
              : undefined
            : [layerVisibilityIcon, displaySettingsIcon, moveIcon, deleteIcon]
        }
        asset={props.asset}
        icons={[ifcWarningIcon, downloadIcon]}
        name={name}
        namePrefix={
          <IconStack>
            <Icon color={color} fixedWidth={true} icon={['fad', icon]} />
            <VersionBadge>v{version}</VersionBadge>
          </IconStack>
        }
      />

      {deleteDialog.render((pos) => (
        <DeleteAssetDialog asset={props.asset} pos={pos} onClose={deleteDialog.hide} />
      ))}
      {displaySettingsDialog.render(
        <DisplaySettingsDialog asset={props.asset} dialog={displaySettingsDialog} />,
      )}
      {glb &&
        layerVisibilityDialog.render((pos) => (
          <LayerVisibilityDialog asset={glb} dialog={layerVisibilityDialog} pos={pos} />
        ))}
    </>
  );
};

const IconStack = styled.div`
  position: relative;
`;

const VersionBadge = styled.div`
  position: absolute;
  left: -25%;
  top: -30%;
  font-size: 0.55em;
  background-color: #fffa;
  padding: 0.2em;
  border-radius: 0.5em;
  line-height: 1em;
  border: 1px solid #ddd;
  font-feature-settings: 'tnum';
`;

export { CadHeader, Props as CadHeaderProps };
